import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useMarketingCampaigns } from '@/hooks/use-marketing';
import {
  PROMOTION_TYPES, campaignStatusLabel, campaignStatusVariant, formatPeriod, promotionLabel,
  type CampaignStatus, type PromotionType,
} from '@/lib/marketing';

export default function AdminMarketingHistory() {
  const { t } = useTranslation();
  const [type, setType] = useState<'all' | PromotionType>('all');
  const [status, setStatus] = useState<'all' | 'COMPLETED' | 'CANCELLED'>('all');
  const [search, setSearch] = useState('');
  const { data: campaigns = [] } = useMarketingCampaigns({ promotion_type: type });

  const rows = campaigns.filter((c: any) =>
    ['COMPLETED', 'CANCELLED'].includes(c.status) &&
    (status === 'all' || c.status === status) &&
    c.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="mx-auto max-w-7xl animate-fade-in p-6">
      <h1 className="mb-1 text-xl font-semibold text-foreground">{t('mkt_history.title')}</h1>
      <p className="mb-5 text-sm text-muted-foreground">{t('mkt_history.subtitle')}</p>

      <div className="mb-4 flex flex-wrap gap-2">
        <Input className="min-w-[200px] flex-1" placeholder={t('mkt_history.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} />
        <Select value={type} onValueChange={(v) => setType(v as any)}>
          <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('mkt_history.allTypes')}</SelectItem>
            {PROMOTION_TYPES.map((pt) => <SelectItem key={pt} value={pt}>{promotionLabel[pt]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => setStatus(v as any)}>
          <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('mkt_history.allStatuses')}</SelectItem>
            <SelectItem value="COMPLETED">{t('mkt_history.completed')}</SelectItem>
            <SelectItem value="CANCELLED">{t('mkt_history.cancelled')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-border bg-card">
        {rows.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">{t('mkt_history.empty')}</p>
        ) : (
          <div className="divide-y divide-border">
            {rows.map((c: any) => (
              <Link key={c.id} to={`/admin/marketing/campaigns/${c.id}`} className="flex flex-wrap items-center justify-between gap-2 p-4 hover:bg-muted/30">
                <div>
                  <p className="text-sm font-medium text-foreground">{c.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {promotionLabel[c.promotion_type as PromotionType]} · {formatPeriod(c.start_at, c.end_at)}
                  </p>
                </div>
                <Badge variant={campaignStatusVariant[c.status as CampaignStatus]}>
                  {campaignStatusLabel[c.status as CampaignStatus]}
                </Badge>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
